<template>
  <div class="wt2-sg wt-text-overlay-field" ref="fieldContainer">
    <div
      v-if="hasLabel || hasHlPresets || allowStoryboardingControls"
      class="-header"
    >
      <div class="-title-box wt2-text-n wt2-color-normal">
        {{ field.label }}
      </div>
      <slot name="tooltip"></slot>
      <div class="-header-right-area">
        <div
          v-if="allowStoryboardingControls"
          @click="lockUnlockStoryboardField"
          class="-lock-icon"
        >
          <i
            class="wticon wticon-unlocked"
            title="Lock Field for Editing"
            v-if="isStoryboardFieldEditable"
          />
          <i
            class="wticon wticon-locked"
            title="Unlock Field for Editing"
            v-else
          />
        </div>
        <div v-if="hasHlPresets" class="-hl-presets-box">
          <highlight-presets
            v-if="hlPresets.length > 1"
            :presets="hlPresets"
            @opened="highlightOptionsOpened"
            @applyPreset="applyPreset"
          />
          <highlight-preset
            v-else
            :preset="hlPresets[0]"
            @applyPreset="applyPreset"
          />
        </div>
      </div>
    </div>

    <template v-if="!allowStoryboardingControls">
      <div
        class="textarea-container"
        @mouseover="onMouseOver"
        @mouseleave="onMouseLeave"
        style="display: flex; align-items: flex-start; gap: 8px"
      >
        <textarea
          ref="textarea"
          dir="auto"
          class="wt2-textarea"
          v-model="textValue"
          @focus="onFocus"
          @blur="onBlur"
          :placeholder="field.description"
          :rows="field.numberOfLines || 5"
          :name="field.name"
        />
        <div
          @click="toggleImportDropdown"
          class="import-text-btn js-import-text-dropdown"
          :class="{ '-active': importMenuOpened }"
          title="Import Text"
          :style="{
            opacity: mouseOver || importMenuOpened ? 1 : 0,
            pointerEvents: mouseOver || importMenuOpened ? '' : 'none',
          }"
        >
          <img
            ref="importTextBtn"
            src="/resources/img/importText.svg"
            alt="Import Text"
            class="js-import-text-dropdown"
          />
        </div>
      </div>
      <div v-if="showTextTooLongMessage" class="wt2-color-error">
        Text is too long... Keep it short or split the asset.
      </div>
    </template>
    <LinkedFieldsTextarea
      v-else
      :textValue="fieldDataModel.linkedFields"
      :originalMandatory="fieldDataModel.mandatory"
      :originalTextValue="textValue"
      :numberOfLines="field.numberOfLines || 5"
      :name="field.name"
      ref="linkedFieldsTextarea"
      @blur="onBlur"
      @change="linkedFieldsChanged"
      @mandatoryChanged="mandatoryChanged"
      :placeholder="field.description"
    />
  </div>
</template>

<script>
import fieldsMixin from '../mixins/fields.vue';
import HighlightPreset from './FieldText.highlightPreset.vue';
import HighlightPresets from './FieldText.highlightPresets.vue';
import LinkedFieldsTextarea from 'components/linkedFields/LinkedFieldsTextarea.vue';

export default {
  components: {
    HighlightPreset,
    HighlightPresets,
    LinkedFieldsTextarea,
  },
  props: {
    field: {
      type: Object,
      required: true,
      validator(value) {
        return _.every(['label', 'name'], (prop) =>
          _.includes(_.keys(value), prop)
        );
      },
    },
    fieldDataModel: {
      type: Object,
      required: false,
    },
    allowStoryboardingControls: {
      type: Boolean,
      default: false,
      required: false,
    },
  },

  mixins: [fieldsMixin],

  data() {
    return {
      focused: false,
      mouseOver: false,
      importMenuOpened: false,
      textValue: null,
      selectionStart: 0,
      selectionEnd: 0,
      sRange: null,
      linkedFields: null,
    };
  },

  created() {
    this.textValue = this.field.value ?? this.field.defaultValue ?? '';
    this.emitChange({ newValue: this.textValue, makeDirty: false });
    this.$watch('textValue', (newValue) => {
      this.emitChange({ newValue });
    });
  },

  mounted() {
    this.fieldReadyNextTick();
  },

  computed: {
    hasLabel() {
      return !_.isEmpty(this.field.label);
    },
    textLength() {
      let text = this.textValue || '';
      if (typeof text !== 'string') {
        text = String(text);
      }
      const presets = this.field.highlightingPresets ?? [];
      for (const p of presets) {
        text = text.replaceAll(p.code, '');
      }
      return text.length;
    },
    showTextTooLongMessage() {
      const maxLength = this.field.maxNumberOfChars || Infinity;
      return this.textLength > maxLength;
    },
    hasHlPresets() {
      return !_.isEmpty(this.hlPresets);
    },
    hlPresets() {
      return this.field.highlightingPresets;
    },

    hasSelection() {
      return this.selectionStart !== this.selectionEnd;
    },

    $textareaRef() {
      return this.$refs.textarea;
    },

    $linkedFieldsTextarea() {
      return this.$refs.linkedFieldsTextarea.$refs.editContainer;
    },

    isStoryboardFieldEditable() {
      return this.fieldDataModel ? this.fieldDataModel.editable : true;
    },
  },

  methods: {
    insertText(text) {
      const curValue = this.textValue?.trim().length
        ? this.textValue + ' '
        : '';
      this.textValue = curValue + text;
    },

    toggleImportDropdown() {
      const fieldContainer = this.$refs.importTextBtn;
      this.$emit('emmiter', {
        name: 'toggle-import-dropdown',
        data: {
          e: fieldContainer,
          component: this,
        },
      });
      this.$nextTick(() => {
        this.importMenuOpened = true;
      });
    },
    closeImportDropdown() {
      this.importMenuOpened = false;
    },
    lockUnlockStoryboardField() {
      this.$emit('storyboardingControlsChanged', {
        name: 'editable',
        value: !this.isStoryboardFieldEditable,
      });
    },

    linkedFieldsChanged(e) {
      this.textValue = e.plainTxt;
      this.$emit('storyboardingControlsChanged', {
        name: 'linkedFields',
        value: e.linkedFields,
      });
    },

    mandatoryChanged(value) {
      this.$emit('storyboardingControlsChanged', { name: 'mandatory', value });
    },

    onFocus(e) {
      this.focused = true;
    },

    onMouseOver(e) {
      this.mouseOver = true;
    },

    onMouseLeave(e) {
      this.mouseOver = false;
    },

    onBlur(e) {
      this.focused = false;
      if (this.$textareaRef) {
        this.selectionStart = e.target.selectionStart;
        this.selectionEnd = e.target.selectionEnd;
      }
    },

    highlightOptionsOpened() {
      if (this.hasSelection) {
        this.$nextTick(() => {
          this.$textareaRef.focus();
          this.$textareaRef.setSelectionRange(
            this.selectionStart,
            this.selectionEnd
          );
        });
      }
    },

    applyPreset(preset) {
      if (this.$textareaRef) {
        this.applyPresetTextarea(preset);
      } else {
        this.applyPresetLinkedFieldsTextarea(preset);
      }
    },

    applyPresetTextarea(preset) {
      const hadSelection = this.hasSelection;
      const body = hadSelection
        ? this.textValue.substring(this.selectionStart, this.selectionEnd)
        : [preset.name ? `${preset.name} ` : '', 'text'].join('');

      const brace = {
        left: preset.code,
        right: [...preset.code].reverse().join(''),
      };

      let shouldRevert = false;
      if (hadSelection && preset.code.length <= this.selectionStart + 1) {
        const ear = {
          left: this.textValue.substring(
            this.selectionStart - brace.left.length,
            this.selectionStart
          ),
          right: this.textValue.substring(
            this.selectionEnd,
            this.selectionEnd + brace.right.length
          ),
        };

        shouldRevert = ear.left === brace.left && ear.right === brace.right;
      }

      if (shouldRevert) {
        // should revert preset if body surrounded by preset.code from both sides
        this.textValue = [
          this.textValue.substring(0, this.selectionStart - brace.left.length),
          body,
          this.textValue.substring(this.selectionEnd + brace.right.length),
        ].join('');

        this.selectionStart -= brace.left.length;
        this.selectionEnd -= brace.left.length;
      } else {
        // wrap
        this.textValue = [
          this.textValue.substring(0, this.selectionStart),
          brace.left,
          body,
          brace.right,
          this.textValue.substring(this.selectionEnd),
        ].join('');

        this.selectionStart += brace.left.length;
        if (hadSelection) {
          this.selectionEnd += brace.left.length;
        } else {
          this.selectionEnd = this.selectionStart + body.length;
        }
      }

      // reselect the body
      this.$nextTick(() => {
        this.$textareaRef.focus();
        this.$textareaRef.setSelectionRange(
          this.selectionStart,
          this.selectionEnd
        );
      });
    },

    applyPresetLinkedFieldsTextarea(preset) {
      const sel = window.getSelection();
      const range = sel.getRangeAt(0);

      if (
        sel.rangeCount > 0 &&
        this.$linkedFieldsTextarea.contains(range.startContainer)
      ) {
        const startNode = range.startContainer;
        const startOffset = range.startOffset;

        const startTextNode = document.createTextNode(preset.code);
        const endTextNode = document.createTextNode(preset.code);

        const boundaryRange = range.cloneRange();
        boundaryRange.collapse(false);
        boundaryRange.insertNode(endTextNode);
        boundaryRange.setStart(startNode, startOffset);
        boundaryRange.collapse(true);
        boundaryRange.insertNode(startTextNode);

        // Reselect the original text
        range.setStartAfter(startTextNode);
        range.setEndBefore(endTextNode);
        sel.removeAllRanges();
        sel.addRange(range);
      }
    },
  },
};
</script>

<style lang="scss">
.wt-text-overlay-field {
  .textarea-container {
    position: relative;
  }

  .import-text-btn {
    position: absolute;
    bottom: 0;
    right: 0;
    cursor: pointer;

    img {
      height: 16px;
      position: relative !important;
      transition: opacity 0.2s ease-in-out;
      margin: 6px 12px 6px 0;
    }

    &:hover,
    &.-active {
      .wticon {
        opacity: 1;
      }
    }
  }
}
</style>
